import React, { useRef, useState } from 'react';

import { motion, useInView } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiSend, FiGithub, FiArrowUpRight } from 'react-icons/fi';
import { FaLinkedinIn } from 'react-icons/fa';
import ScrollGrid from './ScrollGrid';
import FlickerText from './FlickerText';
import './Contact.css';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setSubmitted(true);
    setFormData({ name: '', email: '', message: '' });
    setTimeout(() => setSubmitted(false), 4000);
  };

  const contactInfo = [
    {
      icon: <FiMail />,
      label: 'Email',
      value: 'jaymarccmerilo@gmail.com',
      href: 'mailto:jaymarccmerilo@gmail.com',
      color: '#7c6aef',
    },
    {
      icon: <FiPhone />,
      label: 'Phone',
      value: '09951388922',
      href: 'tel:09951388922',
      color: '#9b6aef',
    },
    {
      icon: <FiMapPin />,
      label: 'Location',
      value: 'Santa Maria, Bulacan, PH',
      href: null,
      color: '#06b6d4',
    },
  ];

  const socials = [
    { icon: <FiGithub />, href: 'https://github.com/emarcj0-debug', label: 'GitHub' },
    { icon: <FaLinkedinIn />, href: 'https://www.linkedin.com/', label: 'LinkedIn' },
    { icon: <FiMail />, href: 'mailto:jaymarccmerilo@gmail.com', label: 'Email' },
  ];

  return (
    <section className="contact" id="contact" ref={ref}>
      <ScrollGrid fadeVariant="fade-center" />
      <div className="container">
        <motion.div
          className="contact-header"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-label">Contact</span>
          <h2 className="section-big-title">
            <FlickerText text="LET'S" isInView={isInView} /><br />
            <span className="accent"><FlickerText text="CONNECT" isInView={isInView} delay={0.3} /></span><span className="muted">.</span>
          </h2>
          <p className="section-description">
            Have a project in mind or want to collaborate? I'm always open to
            discussing new ideas and opportunities.
          </p>
        </motion.div>

        <div className="contact-grid">
          {/* Left - Info + Socials */}
          <motion.div
            className="contact-info-section"
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0.3, x: -20 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Contact Details */}
            <div className="contact-detail-cards">
              {contactInfo.map((info, index) => (
                <motion.a
                  key={index}
                  href={info.href || '#'}
                  className="contact-detail-item glass clickable"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  whileHover={{ y: -4, borderColor: `${info.color}40` }}
                >
                  <div className="detail-icon" style={{ background: `${info.color}12`, color: info.color }}>
                    {info.icon}
                  </div>
                  <div className="detail-text">
                    <span className="detail-label">{info.label}</span>
                    <span className="detail-value">{info.value}</span>
                  </div>
                  {info.href && <FiArrowUpRight className="detail-arrow" />}
                </motion.a>
              ))}
            </div>

            {/* Social Links */}
            <div className="contact-socials">
              <span className="socials-label">Find me on</span>
              <div className="socials-row">
                {socials.map((s, i) => (
                  <a
                    key={i}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social-link clickable"
                    aria-label={s.label}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            className="contact-form-section"
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0.3, x: 20 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <form className="contact-form glass" onSubmit={handleSubmit}>
              <div className="form-title-row">
                <span className="form-dot" />
                <h3>Send a message</h3>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project..."
                  rows="5"
                  required
                />
              </div>

              <button
                type="submit"
                className="btn-primary submit-btn clickable"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="btn-loading">Sending...</span>
                ) : (
                  <>
                    <FiSend />
                    Send Message
                  </>
                )}
              </button>

              {submitted && (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  Message sent successfully! I'll get back to you soon.
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
